import { ref, type Ref } from 'vue';
import { useAuthStore } from '@/store/auth.pinia';
import { useSession } from '@/helpers/use/useSessionHelper';
import { useOAuth } from '@/helpers/use/useOAuthHelper';
import { useNetworkStore } from '@/store';
import type { AuthFormModel } from '@/types/auth';
import type { ChangeNetworkPayload } from '@/types/network';

type AuthOptions = {
  mode: 'form' | 'oauth' | 'oauthApi';
  credentials?: AuthFormModel; // form & oauthApi
  uri?: string; // custom-domain / uri flows
  isModal?: boolean;
  platform: 'web' | 'native' | 'electron';
};

type UseAuthFlow = {
  /**
   * @description ...
   */
  readonly loading: Ref<boolean>;
  /**
   * @description ...
   */
  readonly error: Ref<string | null>;
  /**
   * @description ...
   */
  login(opts: AuthOptions): Promise<void>;
  /**
   * @description Now it's just a wrapper for useSessionHelper.logOut()
   * @todo Later logic from useSessionHelper will be moved here
   */
  logout(): Promise<void>;
};

export function useAuthFlow(): UseAuthFlow {
  const loading = ref(false);
  const error = ref<string | null>(null);

  const { accessTokenByFormOAuthApi } = useAuthStore();
  const { networksByForm, networksByUri, networksByUser } = useNetworkStore();
  const { changeNetwork, logOut } = useSession();
  const { webPopupOAuth, nativePopupOAuth } = useOAuth();

  //#region Private methods
  async function _handleFormLogin({ credentials }: AuthOptions) {
    if (!credentials) throw new Error('Credentials required for form login');

    const networks = await networksByForm(credentials);
    if (!networks?.length) throw new Error('Invalid credentials or no networks found');

    const payload: ChangeNetworkPayload = {
      networks,
      formData: credentials,
      isNetworkSwitch: false,
      isOAuthApi: false,
    };
    await changeNetwork(payload);
  }

  async function _handleOAuthApiLogin({ credentials }: AuthOptions) {
    if (!credentials) throw new Error('Credentials required for OAuth-API login');

    await accessTokenByFormOAuthApi(credentials);

    const networks = await networksByUser();
    if (!networks?.length) throw new Error('No networks available for user');

    const payload: ChangeNetworkPayload = {
      networks,
      formData: credentials,
      isNetworkSwitch: false,
      isOAuthApi: true,
    };
    await changeNetwork(payload);
  }

  async function _handleOAuthLogin({ platform }: AuthOptions) {
    const link = await networksByUri();
    if (!link) throw new Error('Failed to obtain OAuth link');

    platform === 'native' ? nativePopupOAuth(link) : webPopupOAuth(link);
    // Popup helper will close & call setupApp via useOAuthHelper on success.
  }
  //#endregion

  //#region Public methods
  async function logout(): Promise<void> {
    await logOut();
  }

  async function login(opts: AuthOptions): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const authHandlers: Record<AuthOptions['mode'], (opts: AuthOptions) => Promise<void>> = {
        form: _handleFormLogin,
        oauthApi: _handleOAuthApiLogin,
        oauth: _handleOAuthLogin,
      };

      const handler = authHandlers[opts.mode];
      if (!handler) {
        throw new Error(`Unknown auth mode: ${opts.mode}`);
      }

      await handler(opts);
    } catch (err: unknown) {
      console.error('[ERROR] useAuthFlow:login', err);
      error.value = err instanceof Error ? err.message : 'Unknown error';
    } finally {
      loading.value = false;
    }
  }

  //#endregion

  return {
    get loading() {
      return loading;
    },
    get error() {
      return error;
    },
    login,
    logout,
  };
}
